import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { err, ok, type ParsedList, type Result, type VideoSummary } from '@visious/core';
import { fetchPopular, fetchTrending } from '../../api/feeds';
import type { ApiProblem } from '../../api/videos';
import { useWatchProgressLookup } from '../../history/useWatchProgress';
import { getPlatform } from '../../platform/instance';
import { writePopularDisabled } from '../../platform/popularCapability';
import { useSettings } from '../../platform/settings';
import { systemClock } from '../../platform/clock';
import { feedStateFor, feedSpecs, type FeedKind, type FeedState } from './model';

/**
 * The React binding for a feed page. Everything it decides is in `model.ts`; what is here is the
 * query, the clock and the region read — the three things that are not pure.
 *
 * `retry: false` for the reason 1c established and 1e re-confirmed: §3's retry, backoff and
 * live in `createApiTransport`, so a second retry loop here would multiply
 * requests against an instance that is already failing. Query owns the cache and the lifecycle, not
 * the failure policy.
 */
export interface FeedBinding {
  readonly heading: string;
  readonly lede: string;
  readonly state: FeedState;
  /** `null` until the platform has booted, and `null` again when no region preference is set. */
  readonly region: string | null;
  readonly retry: () => void;
  readonly refreshing: boolean;
  /** The grid search — display-only, so it is state here and never in the URL. */
  readonly search: string;
  readonly setSearch: (value: string) => void;
}

type FeedResult = Result<ParsedList<VideoSummary>, ApiProblem>;

async function loadFeed(kind: FeedKind, region: string | null): Promise<FeedResult> {
  const boot = await getPlatform();
  if (!boot.ok) {
    // §3's instance resolution failing is a configuration problem rather than a request problem, and
    // it is reported as one: `bad-url` carries the path so the page can name what could not be built.
    return err({ kind: 'bad-url', path: kind === 'popular' ? '/api/v1/popular' : '/api/v1/trending' });
  }

  const platform = boot.platform;
  if (kind !== 'popular') return await fetchTrending(platform, region);

  const result = await fetchPopular(platform);
  // The Popular menu item's one gate: the instance disabling the endpoint is only ever
  // visible here, so this answer is where the shell and the settings page learn it from.
  if (result.ok) writePopularDisabled(false);
  else if (result.error.kind === 'transport' && result.error.error.kind === 'endpoint-disabled') {
    writePopularDisabled(true);
  }
  return result;
}

/**
 * `enabled: false` is what the popular page's trending section costs when it is switched off:
 * the hook still runs — hooks cannot be conditional — but the query does not, so no reader pays a
 * request for a section that is not drawn. The state stays `loading`, which nothing renders.
 */
export function useFeed(kind: FeedKind, options: { readonly enabled?: boolean } = {}): FeedBinding {
  const spec = feedSpecs()[kind];
  /*
   * From the settings *store* rather than a bare `readContentRegion()` (3e). The value is the same; what
   * the store adds is that changing the region on the settings page — or in another tab — re-renders this
   * hook, so the query key below changes and the new region is fetched. Reading storage directly would
   * only pick it up on the next mount, which is a stale Trending page that looks like a cache bug.
   */
  const contentRegion = useSettings().contentRegion;
  const region = kind === 'trending' ? contentRegion : null;

  const enabled = options.enabled !== false;
  const query = useQuery({
    // The region is part of the identity: two regions are two different lists, and sharing one cache
    // entry would show the previous region's rows after the setting changes (3e).
    queryKey: ['feed', kind, region],
    queryFn: () => loadFeed(kind, region),
    enabled,
    retry: false,
    // A feed is a list of what is current, so it goes stale quickly — but not so quickly that
    // clicking a video and pressing Back re-fetches 50 KB to show the same rows.
    staleTime: 5 * 60 * 1000,
  });

  // 4b — read from `localStorage`, so a marker on a page that works signed out costs no request. The
  // decision the Known-gaps entry left open: the *history's* id set would have needed one.
  const progress = useWatchProgressLookup();
  const [search, setSearch] = useState('');

  const state = useMemo(() => {
    // A disabled query is also `isPending`, which lands on the loading state — right by accident and by
    // intent: the caller that disabled it renders nothing at all.
    if (query.isPending) return feedStateFor(kind, null, { nowEpochSec: 0, progress, search });

    const result: FeedResult =
      query.error !== null
        ? // A thrown query is a bug or an abort rather than a classified failure — the transport
          // returns its errors as values. Reported as a network problem, which is the honest reading
          // of "the request did not come back with an answer".
          err({ kind: 'transport', error: { kind: 'network', status: null, url: '', retryable: true } })
        : (query.data ?? ok({ items: [], dropped: [] }));

    // The dates are relative to the moment the rows *arrived*, not to this render: a page left open
    // for an hour would otherwise creep from "2 hours ago" to "3 hours ago" on every unrelated
    // re-render, while the list itself is an hour stale. `dataUpdatedAt` is 0 before the first fetch.
    const arrivedAt = query.dataUpdatedAt > 0 ? query.dataUpdatedAt : systemClock.nowEpochMs();

    return feedStateFor(kind, result, { nowEpochSec: Math.floor(arrivedAt / 1000), progress, search });
  }, [kind, progress, query.data, query.dataUpdatedAt, query.error, query.isPending, search]);

  return {
    heading: spec.heading,
    lede: spec.lede,
    state,
    region,
    retry: () => void query.refetch(),
    refreshing: query.isFetching && !query.isPending,
    search,
    setSearch,
  };
}
